import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import RadarChartCard from './RadarChartCard';
import type { PsychologicalRadarGroup } from './model';
import MetricCard from '@/components/common/MetricCard';
import Section from '@/components/common/Section';

export interface CategoryBreakdownRadarSectionProps {
    title: string;
    subtitle?: string;
    iconKey?: string;
    accent?: 'purple' | 'blue' | 'green' | 'teal' | 'muted' | 'none';
    radarGroups: PsychologicalRadarGroup[];
}

const CategoryBreakdownRadarSection = ({
    title,
    subtitle,
    iconKey,
    accent = 'blue',
    radarGroups,
}: CategoryBreakdownRadarSectionProps) => {
    const { t } = useTranslation('traineeReports');

    const sections = useMemo(() => radarGroups, [radarGroups]);

    return (
        <div className="col-span-8">
            <Section title={title} subtitle={subtitle} iconKey={iconKey} accent={accent}>
                <div className="space-y-6">
                    {sections.map(s => (
                        <div key={s.key} className="space-y-5">
                            <div className="flex w-full gap-5 overflow-x-auto pb-1">
                                {s.scoreCards.map(c => (
                                    <MetricCard
                                        key={c.label}
                                        label={c.label}
                                        value={c.value || t('na')}
                                        variant="plain"
                                        minWidth={150}
                                    />
                                ))}
                            </div>

                            <div className={`grid gap-6 ${s.charts.length > 1 ? 'sm:grid-cols-2' : 'sm:grid-cols-1'}`}>
                                {s.charts.map((c, idx) => (
                                    <div key={idx} className="rounded-xl bg-white p-6">
                                        <RadarChartCard
                                            data={c.data}
                                            maxValueData={c.maxDomain ?? 10}
                                            series={c.series}
                                            showTeam={c.series ? undefined : !!c.showTeam}
                                            variant={c.variant ?? 'bare'}
                                            height={c.height ?? 280}
                                            footer={c.footer}
                                        />
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            </Section>
        </div>
    );
};

export default CategoryBreakdownRadarSection;
